import Logger from './logger';

class LoggerWrapper {
	static get DEBUG() {
		return 50;
	}

	static get INFO() {
		return 40;
	}

	static get WARNING() {
		return 30;
	}

	static get ERROR() {
		return 20;
	}

	static get CRITICAL() {
		return 10;
	}

	static get QUIET() {
		return 0;
	}

	constructor(category, className, verbosity) {
		this._setDefaults();

		this.setCategory(category);
		this.setClassName(className);
		this.setVerbosity(verbosity);
	}

	info(msg, extraData, params) {
		this._log(msg, extraData, params, Logger.INFO);
	}

	error(msg, extraData, params) {
		this._log(msg, extraData, params, Logger.ERROR);
	}

	critical(msg, extraData, params) {
		this._log(msg, extraData, params, Logger.CRITICAL);
	}

	debug(msg, extraData, params) {
		this._log(msg, extraData, params, Logger.DEBUG);
	}

	warn(msg, extraData, params) {
		this._log(msg, extraData, params, Logger.WARN);
	}

	setVerbosity(verbosity) {
		if (verbosity && Object.values(this.verbosity).includes(verbosity)) {
			this._localVerbosity = verbosity;
			this._remoteVerbosity = verbosity;
		}
	}

	setLocalVerbosity(verbosity) {
		if (Object.values(this.verbosity).includes(verbosity)) {
			this._localVerbosity = verbosity;
		}
	}

	setRemoteVerbosity(verbosity) {
		if (Object.values(this.verbosity).includes(verbosity)) {
			this._remoteVerbosity = verbosity;
		}
	}

	setAutoFunction(boolean) {
		this._autoFunction = !!boolean;
	}

	setClassName(className) {
		if (className) {
			this._className = className;
		}
	}

	setTopic(topic) {
		if (topic) {
			this._topic = topic;
		}
	}

	setCategory(category) {
		if (category) {
			this._category = category;
		}
	}

	setFunctionName(functionName) {
		if (functionName) {
			this._functionName = functionName;
		}
	}

	setDefaultsExtraData(defaults) {
		if (defaults instanceof Object || defaults === null) {
			this._defaultsExtraData = defaults;
		}
	}

	_setDefaults() {
		this.verbosity = {
			DEBUG: LoggerWrapper.DEBUG,
			INFO: LoggerWrapper.INFO,
			WARN: LoggerWrapper.WARN,
			ERROR: LoggerWrapper.ERROR,
			CRITICAL: LoggerWrapper.CRITICAL,
			QUIET: LoggerWrapper.QUIET,
		};

		this._className = null;
		this._platform = null;
		this._autoFunction = true;
		this._topic = null;
		this._category = null;
		this._functionName = null;
		this._defaultsExtraData = null;
		this._allowedParams = {
			extraData: null,
			topic: null,
			user: null,
			slug: null,
			err: null,
			functionName: null,
			category: null,
		};

		this._localVerbosity = this.verbosity.INFO; // DEBUG | debug > info > warn > error > critical
		this._remoteVerbosity = this.verbosity.INFO; // DEBUG | debug > info > warn > error > critical
	}

	_getLoggerConfig(severity = Logger.INFO, params = {}) {
		const config = {
			severity,
		};

		Object.keys(params).forEach((value) => {
			if (this._allowedParams.hasOwnProperty(value) && params[value] !== null) {
				config[value] = params[value];
			}
		});

		try {
			if (config.err) {
				if (!config.err.stack) {
					config.err.stack = '';
				}

				if (!config.err.message) {
					config.err.message = 'EMPTY';
				}
			}
		} catch (err) {}

		if (this._autoFunction && !params.functionName && !this._functionName) {
			const functionName = this._getParentFunctionName();
			if (functionName) {
				config.functionName = functionName;
			}
		} else if (this._functionName && !params.functionName) {
			config.functionName = this._functionName;
		}

		if (this._className) {
			config.className = this._className;
		}

		if (this._topic && !params.topic) {
			config.topic = this._topic;
		}

		if (this._category && !params.category) {
			config.category = this._category;
		}

		if (params.logMemoryStats) {
			try {
				const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = window.performance.memory;
				config.memory = { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize };
			} catch (e) {}
		}

		return config;
	}

	_getParentFunctionName() {
		const e = new Error();
		const targeted = e.stack.split('\n').filter((l) => l != 'Error' && !l.includes('LoggerWrapper'))[0];
		const matched = targeted && targeted.match(/at ((\w+)\.?)+/) ? targeted.match(/at ((\w+)\.?)+/)[2] : null;

		return matched !== '_callee' && matched !== 'eval' ? matched : null;
	}

	_checkLevel(severity, verbosity) {
		let rt = true;
		if (verbosity < this.verbosity.DEBUG) {
			if (verbosity > this.verbosity.QUIET) {
				rt = severity === Logger.ERROR && verbosity >= this.verbosity.ERROR;
				rt = rt || (severity === Logger.WARN && verbosity >= this.verbosity.WARN);
				rt = rt || (severity === Logger.INFO && verbosity >= this.verbosity.INFO);
			} else {
				rt = false;
			}
		}

		return rt;
	}

	_log(msg, extraData, params = {}, severity) {
		if (!params) {
			params = {};
		}

		if (extraData || this._defaultsExtraData) {
			if (this._defaultsExtraData) {
				if (extraData instanceof Object) {
					params.extraData = { ...this._defaultsExtraData, extraData };
				} else {
					params.extraData = { ...this._defaultsExtraData, value: extraData };
				}
			} else {
				params.extraData = extraData;
			}
		}

		const config = {
			...this._getLoggerConfig(severity, params),
			severity,
			msg,
		};

		this._localLog(config);
		this._remoteLog(config);
	}

	_remoteLog(config) {
		if (this._checkLevel(config.severity, this._remoteVerbosity)) {
			let parsedExtraData = config.extraData || null;

			if (parsedExtraData !== null && !(parsedExtraData instanceof String) && !(parsedExtraData instanceof Object)) {
				parsedExtraData = new String(parsedExtraData);
			}
			Logger.log(
				config.severity,
				config.msg,
				config.className || null,
				config.functionName || null,
				config.topic,
				parsedExtraData,
				config.err || null,
				config.category || null
			);
		}
	}

	_localLog(params) {
		if (console && this._checkLevel(params.severity, this._localVerbosity)) {
			let out = console.log;
			switch (params.severity) {
				case Logger.INFO:
					out = console.info;
					break;
				case Logger.ERROR:
					out = console.error;
					break;
				case Logger.CRITICAL:
					out = console.error;
					break;
				case Logger.DEBUG:
					out = console.debug;
					break;
				case Logger.WARN:
					out = console.warn;
					break;
			}

			if (out) {
				const lessParams = { ...params };
				delete lessParams.msg;
				delete lessParams.className;
				delete lessParams.severity;
				delete lessParams.topic;
				delete lessParams.category;
				delete lessParams.extraData;

				const args = [`[${params.className}]`, params.msg];
				if (params.extraData !== null && params.extraData !== undefined) {
					args.push(params.extraData);
				}

				if (Object.keys(lessParams).length) {
					args.push(lessParams);
				}

				out.apply(this, args);
			}
		}
	}
}

module.exports = LoggerWrapper;

export default LoggerWrapper;
